export const WEB_INVITE_SECTION_CATALOG = [
  { type: 'hero', label: 'Hero', description: 'Names, celebration date, cover image and the opening message.', defaultVariant: 'classic' },
  { type: 'countdown', label: 'Countdown', description: 'A live countdown to the first celebration.', defaultVariant: 'cards' },
  { type: 'story', label: 'Our story', description: 'A personal note, story or welcome from the hosts.', defaultVariant: 'editorial' },
  { type: 'hosts', label: 'Meet the hosts', description: 'Introduce the couple, honouree or hosting family.', defaultVariant: 'portraits' },
  { type: 'itinerary', label: 'Itinerary', description: 'Event details, timings, dress codes and venues.', defaultVariant: 'timeline' },
  { type: 'venue', label: 'Venues', description: 'Venue cards and addresses from event details.', defaultVariant: 'cards' },
  { type: 'map', label: 'Venue map', description: 'An interactive map for one selected event location.', defaultVariant: 'paper_map' },
  { type: 'gallery', label: 'Gallery', description: 'A curated collection of event and couple photos.', defaultVariant: 'mosaic' },
  { type: 'wishes', label: 'Guest wishes', description: 'Public wishes shared by guests.', defaultVariant: 'carousel' },
  { type: 'rsvp', label: 'RSVP', description: 'A sign-in protected call to confirm attendance.', defaultVariant: 'card' },
  { type: 'registry', label: 'Registry', description: 'A gentle link to the gift registry or wishlist.', defaultVariant: 'card' },
  { type: 'contacts', label: 'Key contacts', description: 'Help guests find the right person to contact.', defaultVariant: 'cards' },
  { type: 'actions', label: 'Guest actions', description: 'Shortcuts for photos, travel, rooms and wardrobe.', defaultVariant: 'tiles' },
  { type: 'footer', label: 'Footer', description: 'A closing note and celebration signature.', defaultVariant: 'minimal' },
];

export const SECTION_VARIANTS = {
  hero: ['classic', 'editorial', 'cinematic', 'minimal', 'garden_frame', 'temple_courtyard', 'royal_tapestry', 'kashi_riverfront'],
  countdown: ['cards', 'inline', 'statement', 'garden_medallions', 'garden_statement', 'muhurtham_dial', 'woven_numerals', 'ghat_countdown'],
  story: ['editorial', 'letter', 'quote', 'vellum_letter', 'stone_prologue', 'illuminated_folio', 'river_letter'],
  hosts: ['portraits', 'split', 'names', 'cameos'],
  itinerary: ['timeline', 'cards', 'compact', 'botanical_cards', 'garden_timeline', 'muhurtham_program', 'ceremony_carousel', 'ghat_programme'],
  venue: ['cards', 'feature', 'minimal', 'estate_feature', 'temple_inscriptions', 'royal_venue', 'riverfront_places'],
  map: ['paper_map', 'full_bleed', 'kolam_map', 'gilded_map', 'river_bend_map'],
  gallery: ['mosaic', 'filmstrip', 'editorial', 'garden_cameos', 'temple_slideshow', 'tapestry_masonry', 'river_editorial'],
  wishes: ['carousel', 'cards', 'quote', 'love_letters', 'blessing_chorus', 'courtly_notes', 'ripple_blessings'],
  rsvp: ['card', 'banner', 'minimal', 'wax_seal', 'betel_leaf', 'royal_decree', 'riverbank_rsvp'],
  registry: ['card', 'minimal'],
  contacts: ['cards', 'minimal', 'botanical_cards', 'family_directory', 'court_directory', 'kashi_directory'],
  actions: ['tiles', 'pills'],
  custom: ['editorial', 'feature', 'minimal'],
  footer: ['minimal', 'signature', 'statement', 'garden_signature', 'courtyard_signature', 'woven_signature', 'dusk_signature'],
};

export const BIRTHDAY_SECTION_VARIANTS = {
  hero: ['birthday_balloon_dinner', 'birthday_champagne_toast', 'birthday_milestone'],
  countdown: ['milestone_countdown'],
  story: ['birthday_note'],
  itinerary: ['party_program'],
  venue: ['celebration_venue'],
  gallery: ['memory_wall'],
  wishes: ['toast_notes'],
  rsvp: ['surprise_rsvp'],
  footer: ['birthday_signature'],
};

export function getSectionVariants(type, eventType = 'wedding') {
  const baseVariants = SECTION_VARIANTS[type] || ['default'];
  if (eventType !== 'birthday') return baseVariants;
  return [...(BIRTHDAY_SECTION_VARIANTS[type] || []), ...baseVariants];
}

export const DEFAULT_WEB_INVITE_LAYOUT = {
  mode: 'stacked',
  hero_panel_position: 'left',
  hero_panel_width: 46,
  background_image_url: null,
  frame_image_url: null,
  decorative_image_url: null,
  background_position: 'center',
  show_frame: false,
  overlay_strength: 0.72,
  section_style: 'default',
  section_divider: 'none',
  mobile_hero_height: 'tall',
};

export const DEFAULT_WEB_INVITE_OPENING = {
  enabled: true,
  eyebrow: 'A celebration awaits',
  title: 'Something beautiful begins',
  message: 'Open the invitation and step into the celebration.',
  action_label: 'Unfold the invitation',
};

export const DEFAULT_WEB_INVITE_MUSIC = {
  enabled: true,
  track_id: 'soft_piano',
  custom_audio_url: null,
  custom_audio_name: null,
  volume: 0.32,
};

export const WEB_INVITE_MUSIC_TRACKS = [
  { id: 'garden_waltz', name: 'Garden Waltz', description: 'Warm, romantic and gently dancing' },
  { id: 'soft_piano', name: 'Soft Piano', description: 'Quiet, timeless and intimate' },
  { id: 'moonlit_strings', name: 'Moonlit Strings', description: 'Dreamy, spacious and cinematic' },
  { id: 'custom', name: 'My uploaded music', description: 'Use an MP3, WAV, OGG, M4A or AAC file' },
];

const baseAccess = {
  view_mode: 'public',
  actions_require_auth: true,
  guest_membership_required: true,
  replace_guest_home: false,
};

const section = (type, order, options = {}) => {
  const definition = WEB_INVITE_SECTION_CATALOG.find((item) => item.type === type);
  return {
    id: type,
    type,
    enabled: options.enabled !== false,
    order,
    variant: options.variant || definition?.defaultVariant || 'default',
    source_mode: options.source_mode || 'event',
    settings: type === 'itinerary'
      ? { include_all_items: true, selected_item_ids: [], ...(options.settings || {}) }
      : type === 'map'
        ? { selected_venue_id: null, ...(options.settings || {}) }
        : type === 'gallery'
          ? { include_all_images: true, selected_image_urls: [], ...(options.settings || {}) }
          : (options.settings || {}),
    overrides: options.overrides || {},
  };
};

const template = ({ id, version = 1, name, description, mood, swatches, theme, layout, effects, opening, music, sections, eventTypes = ['wedding'] }) => ({
  id,
  name,
  description,
  mood,
  swatches,
  eventTypes,
  config: {
    schema_version: 1,
    template_id: id,
    template_version: version,
    customization_mode: 'guided',
    sync_mode: 'live',
    theme,
    layout: { ...DEFAULT_WEB_INVITE_LAYOUT, ...(layout || {}) },
    effects,
    opening: { ...DEFAULT_WEB_INVITE_OPENING, ...(opening || {}) },
    music: { ...DEFAULT_WEB_INVITE_MUSIC, ...(music || {}) },
    seo: { title: null, description: null, share_image_url: null, keywords: [] },
    access: baseAccess,
    sections,
  },
});

const BIRTHDAY_TEMPLATE_IDS = new Set([
  'galago.birthday-velvet-surprise-60',
  'galago.birthday-champagne-bokeh-50',
  'galago.birthday-balloon-dinner-30',
]);

export const WEB_INVITE_TEMPLATES = [
  template({
    id: 'galago.birthday-velvet-surprise-60',
    name: 'Velvet Surprise',
    description: 'A secret-envelope opening, candlelit burgundy stationery, champagne typography and milestone birthday moments for a polished surprise party.',
    mood: 'Elegant · Secret',
    swatches: ['#5B0F2E', '#D7B56D', '#FFF3E5'],
    eventTypes: ['birthday'],
    theme: {
      primary_color: '#5B0F2E', secondary_color: '#D7B56D', accent_color: '#DFA1A5',
      background_color: '#FFF3E5', text_color: '#2B1720', heading_font: 'Playfair Display',
      body_font: 'Inter', surface_style: 'paper', corner_style: 'soft', spacing: 'comfortable',
    },
    layout: {
      mode: 'stacked', hero_panel_position: 'left', hero_panel_width: 48,
      background_image_url: '/assets/web-invites/birthday-velvet-surprise/velvet-surprise-bg.png',
      frame_image_url: null,
      decorative_image_url: '/assets/web-invites/birthday-velvet-surprise/velvet-surprise-bg.png',
      background_position: 'center', show_frame: false, overlay_strength: 0.36,
      section_style: 'default', section_divider: 'none', mobile_hero_height: 'full',
    },
    effects: {
      hero_effect: 'golden_dust', intensity: 0.55, speed: 0.2, scroll_reveal: 'rise',
      parallax: false, pointer_interaction: true, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'Shhh... it is a surprise',
      title: 'A secret 60th celebration',
      message: 'Help us keep the joy under wraps until the big reveal. Tap when you are somewhere private.',
      action_label: 'Open the invitation',
    },
    music: { track_id: 'moonlit_strings', volume: 0.24 },
    sections: [
      section('hero', 0, {
        variant: 'birthday_milestone',
        overrides: {
          eyebrow: 'You are invited to a surprise 60th birthday',
          headline: '60',
          body: 'Join us for an unforgettable evening of memories, laughter, dinner, music and a surprise toast.',
        },
      }),
      section('countdown', 1, { variant: 'milestone_countdown', overrides: { headline: 'Until the secret is out' } }),
      section('story', 2, { variant: 'birthday_note', overrides: { headline: 'About the celebration', body: 'This day is not just about a birthday. It is about celebrating a life, a family, and the memories we are lucky to share.' } }),
      section('itinerary', 3, { variant: 'party_program', overrides: { headline: 'Program highlights' } }),
      section('map', 5, { variant: 'paper_map' }),
      section('gallery', 6, { variant: 'memory_wall', overrides: { headline: 'A few favorite memories' } }),
      section('wishes', 7, { variant: 'toast_notes', overrides: { headline: 'Leave a birthday wish' } }),
      section('rsvp', 8, { variant: 'surprise_rsvp', overrides: { headline: 'Kindly RSVP' } }),
      section('contacts', 9, { variant: 'minimal', overrides: { headline: 'Your surprise hosts' } }),
      section('footer', 10, { variant: 'birthday_signature', overrides: { headline: 'With love, from the hosts', body: 'Please keep this celebration a surprise.' } }),
    ],
  }),
  template({
    id: 'galago.birthday-champagne-bokeh-50',
    name: 'Toast Reveal',
    description: 'A birthday invite that opens with a cork-pop toast reveal, then unfolds through champagne bubbles, label-style typography and golden milestone moments.',
    mood: 'Interactive · Toast',
    swatches: ['#7A0F3F', '#D7A54F', '#F8DDE5'],
    eventTypes: ['birthday'],
    theme: {
      primary_color: '#7A0F3F', secondary_color: '#D7A54F', accent_color: '#E38CA4',
      background_color: '#FFF0E6', text_color: '#2B101B', heading_font: 'Cormorant Garamond',
      body_font: 'Inter', surface_style: 'glass', corner_style: 'soft', spacing: 'comfortable',
    },
    layout: {
      mode: 'stacked', hero_panel_position: 'left', hero_panel_width: 48,
      background_image_url: '/assets/web-invites/birthday-champagne-bokeh/champagne-bokeh-bg.png',
      frame_image_url: null,
      bottle_image_url: '/assets/web-invites/birthday-champagne-bokeh/champagne-bottle-premium-burgundy-static.png',
      cork_image_url: '/assets/web-invites/birthday-champagne-bokeh/champagne-cork-only.png',
      decorative_image_url: '/assets/web-invites/birthday-champagne-bokeh/champagne-bokeh-bg.png',
      background_position: 'center', show_frame: false, overlay_strength: 0.32,
      section_style: 'champagne_toast', section_divider: 'bubble_stream', mobile_hero_height: 'full',
    },
    effects: {
      hero_effect: 'golden_dust', intensity: 0.72, speed: 0.18, scroll_reveal: 'rise',
      parallax: false, pointer_interaction: true, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'The toast is ready',
      title: 'A milestone birthday toast',
      message: 'Tap the bottle cork to reveal the celebration.',
      action_label: 'Pop the cork',
    },
    music: { track_id: 'moonlit_strings', volume: 0.24 },
    sections: [
      section('hero', 0, {
        variant: 'birthday_champagne_toast',
        overrides: {
          eyebrow: 'Please join us in celebration of',
          milestone: '50',
          body: 'An elegant evening of cocktails, dinner, music and birthday wishes, poured with love.',
        },
      }),
      section('countdown', 1, { variant: 'milestone_countdown', overrides: { eyebrow: 'The golden countdown', headline: 'Until the toast begins' } }),
      section('story', 2, { variant: 'birthday_note', overrides: { eyebrow: 'A card from the table', headline: 'A toast to a beautiful life', body: 'We are gathering the people who make this life brighter for one unforgettable milestone celebration.' } }),
      section('itinerary', 3, { variant: 'party_program', overrides: { eyebrow: 'The dinner programme', headline: "Tonight's celebration", body: 'An evening arranged in courses, moments and music.' } }),
      section('map', 5, { variant: 'paper_map' }),
      section('gallery', 6, { variant: 'memory_wall', overrides: { eyebrow: 'The memory table', headline: 'Memories from the years', body: 'Photographs gathered like favorite stories around the table.' } }),
      section('wishes', 7, { variant: 'toast_notes', overrides: { eyebrow: 'Words worth keeping', headline: 'Raise a glass', body: 'Birthday wishes from the people who make every year brighter.' } }),
      section('rsvp', 8, { variant: 'surprise_rsvp', overrides: { eyebrow: 'Reserved for you', headline: 'Your place awaits', body: 'Let the hosts know whether we should set a place for you.' } }),
      section('contacts', 9, { variant: 'minimal', overrides: { eyebrow: 'At your service', headline: "The hosts' table", body: 'Questions about the evening? The hosts are close at hand.' } }),
      section('footer', 10, { variant: 'birthday_signature', overrides: { eyebrow: 'The final toast', headline: "Here's to 50 beautiful years", body: 'And to many more.' } }),
    ],
  }),
  template({
    id: 'galago.birthday-balloon-dinner-30',
    name: 'Balloon Dinner Reveal',
    description: 'A video-style birthday dinner invite where the birthday title clears, balloons lift away and a detail screen appears over a silver-gold balloon gift scene.',
    mood: 'Cinematic · Dinner',
    swatches: ['#263848', '#D9B26A', '#F5F0EA'],
    eventTypes: ['birthday'],
    theme: {
      primary_color: '#263848', secondary_color: '#D9B26A', accent_color: '#B88E6D',
      background_color: '#F5F0EA', text_color: '#172332', heading_font: 'Cormorant Garamond',
      body_font: 'Inter', surface_style: 'glass', corner_style: 'soft', spacing: 'comfortable',
    },
    layout: {
      mode: 'stacked', hero_panel_position: 'left', hero_panel_width: 48,
      background_image_url: '/assets/web-invites/birthday-balloon-dinner/balloon-gift-bg.png',
      frame_image_url: null,
      decorative_image_url: '/assets/web-invites/birthday-balloon-dinner/balloon-gift-bg.png',
      background_position: 'center', show_frame: false, overlay_strength: 0.3,
      section_style: 'balloon_dinner', section_divider: 'bubble_stream', mobile_hero_height: 'full',
    },
    effects: {
      hero_effect: 'golden_dust', intensity: 0.32, speed: 0.16, scroll_reveal: 'rise',
      parallax: false, pointer_interaction: true, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'You are invited to',
      title: 'Birthday Dinner',
      message: 'Release the balloons and reveal the party details.',
      action_label: 'Release the balloons',
    },
    music: { track_id: 'garden_waltz', volume: 0.24 },
    sections: [
      section('hero', 0, {
        variant: 'birthday_balloon_dinner',
        overrides: {
          eyebrow: 'You are invited to',
          headline: 'Birthday Dinner',
          name: 'Oliver',
          milestone: '30',
          body: 'RSVP by December 10',
          time_label: '11 AM - 1 PM',
          attire: 'Chic black and white attire',
        },
      }),
      section('countdown', 1, { variant: 'milestone_countdown', overrides: { eyebrow: 'Dinner countdown', headline: 'Until dinner begins' } }),
      section('story', 2, { variant: 'birthday_note', overrides: { headline: 'A note from the hosts', body: 'Join us for a warm birthday dinner, sparkling balloons and a table full of favorite people.' } }),
      section('itinerary', 3, { variant: 'party_program', overrides: { headline: 'Dinner flow' } }),
      section('map', 5, { variant: 'paper_map' }),
      section('gallery', 6, { variant: 'memory_wall', overrides: { headline: 'Dinner memories' } }),
      section('wishes', 7, { variant: 'toast_notes', overrides: { headline: 'Leave a birthday note' } }),
      section('rsvp', 8, { variant: 'surprise_rsvp', overrides: { headline: 'Kindly RSVP' } }),
      section('contacts', 9, { variant: 'minimal', overrides: { headline: 'Questions?' } }),
      section('footer', 10, { variant: 'birthday_signature', overrides: { headline: 'See you at dinner', body: 'Come ready for good food, bright lights and birthday wishes.' } }),
    ],
  }),
  template({
    id: 'galago.swan-garden-romance',
    name: 'Swan Garden Romance',
    description: 'A storybook garden framed by antique stone, painterly florals and a quiet swan lake.',
    mood: 'Romantic · Storybook',
    swatches: ['#6F1832', '#C99A83', '#F4E8DA'],
    theme: {
      primary_color: '#6F1832', secondary_color: '#C99A83', accent_color: '#A25B67',
      background_color: '#F4E8DA', text_color: '#3D2928', heading_font: 'Great Vibes',
      body_font: 'Libre Baskerville', surface_style: 'paper', corner_style: 'soft', spacing: 'compact',
    },
    layout: {
      mode: 'stacked', hero_panel_position: 'left', hero_panel_width: 48,
      background_image_url: '/assets/web-invites/swan-garden/garden-lake.png',
      frame_image_url: '/assets/web-invites/swan-garden/floral-arch.png',
      decorative_image_url: '/assets/web-invites/swan-garden/floral-corner-v2.png',
      background_position: 'center', show_frame: true, overlay_strength: 0.12,
      section_style: 'botanical_paper', section_divider: 'botanical', mobile_hero_height: 'full',
    },
    effects: {
      hero_effect: 'golden_dust', intensity: 0.85, speed: 0.24, scroll_reveal: 'rise',
      parallax: false, pointer_interaction: true, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'The garden gates are opening',
      title: 'Step into our forever',
      message: 'A day of love, laughter and beautiful beginnings is waiting just beyond these gates.',
      action_label: 'Enter the garden',
    },
    music: { track_id: 'garden_waltz', volume: 0.28 },
    sections: [
      section('hero', 0, { variant: 'garden_frame' }),
      section('countdown', 1, { variant: 'garden_statement' }),
      section('story', 2, { variant: 'vellum_letter' }),
      section('itinerary', 3, { variant: 'garden_timeline' }),
      section('venue', 4, { variant: 'estate_feature' }),
      section('map', 5, { variant: 'paper_map' }),
      section('gallery', 6, { variant: 'garden_cameos' }),
      section('wishes', 7, { variant: 'love_letters' }),
      section('rsvp', 8, { variant: 'wax_seal' }),
      section('contacts', 9, { variant: 'botanical_cards' }),
      section('footer', 10, { variant: 'garden_signature' }),
    ],
  }),
  template({
    id: 'galago.south-indian-temple-brass',
    version: 2,
    name: 'South Indian Temple Brass',
    description: 'Ring a temple bell at first light, then move through a cinematic courtyard, wedding portraits and a day-by-day muhūrtham programme.',
    mood: 'Sacred · Dawn-lit',
    swatches: ['#4A1712', '#C79A43', '#2F5B45'],
    theme: {
      primary_color: '#4A1712', secondary_color: '#C79A43', accent_color: '#2F5B45',
      background_color: '#EAD9B8', text_color: '#2B231C', heading_font: 'Cormorant Garamond',
      body_font: 'Libre Baskerville', surface_style: 'flat', corner_style: 'soft', spacing: 'compact',
    },
    layout: {
      mode: 'stacked', hero_panel_position: 'left', hero_panel_width: 48,
      background_image_url: '/assets/web-invites/south-indian-temple-brass/temple-dawn-courtyard-v2.jpg',
      frame_image_url: null,
      decorative_image_url: '/assets/web-invites/south-indian-temple-brass/temple-bell-v2.png',
      background_position: 'center', show_frame: false, overlay_strength: 0.4,
      section_style: 'temple_courtyard', section_divider: 'temple_steps', mobile_hero_height: 'full',
    },
    effects: {
      hero_effect: 'golden_dust', intensity: 0.46, speed: 0.18, scroll_reveal: 'fade',
      parallax: false, pointer_interaction: true, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'At first light, two families gather',
      title: 'Ring in our new beginning',
      message: 'Let the bell carry our joy through the courtyard and into the celebration.',
      action_label: 'Ring the wedding bell',
    },
    music: { track_id: 'garden_waltz', volume: 0.23 },
    sections: [
      section('hero', 0, { variant: 'temple_courtyard', overrides: { eyebrow: 'With the blessings of our families' } }),
      section('story', 1, { variant: 'stone_prologue', overrides: { headline: 'A promise at first light' } }),
      section('gallery', 2, { variant: 'temple_slideshow', overrides: { headline: 'Portraits before the ceremony' } }),
      section('countdown', 3, { variant: 'muhurtham_dial', overrides: { headline: 'Until the auspicious hour' } }),
      section('itinerary', 4, { variant: 'muhurtham_program', overrides: { headline: 'The muhūrtham programme' } }),
      section('venue', 5, { variant: 'temple_inscriptions', overrides: { headline: 'Where our families gather' } }),
      section('map', 6, { variant: 'kolam_map' }),
      section('wishes', 7, { variant: 'blessing_chorus', overrides: { headline: 'Blessings carried to us' } }),
      section('rsvp', 8, { variant: 'betel_leaf', overrides: { headline: 'Take your place with us' } }),
      section('contacts', 9, { variant: 'family_directory' }),
      section('footer', 10, { variant: 'courtyard_signature' }),
    ],
  }),
  template({
    id: 'galago.royal-tapestry',
    name: 'Royal Tapestry',
    description: 'A photo-led royal folio framed by midnight velvet, ruby silk and dimensional antique-gold embroidery.',
    mood: 'Regal · Heirloom',
    swatches: ['#1D2145', '#8A2636', '#C7A052'],
    theme: {
      primary_color: '#1D2145', secondary_color: '#C7A052', accent_color: '#8A2636',
      background_color: '#F2E8D1', text_color: '#282137', heading_font: 'Cormorant Garamond',
      body_font: 'Libre Baskerville', surface_style: 'flat', corner_style: 'square', spacing: 'compact',
    },
    layout: {
      mode: 'stacked', hero_panel_position: 'left', hero_panel_width: 48,
      background_image_url: '/assets/web-invites/royal-tapestry/royal-tapestry-mobile.jpg',
      frame_image_url: null, decorative_image_url: null,
      background_position: 'center', show_frame: false, overlay_strength: 0.38,
      section_style: 'tapestry_folio', section_divider: 'woven_band', mobile_hero_height: 'full',
    },
    effects: {
      hero_effect: 'none', intensity: 0.7, speed: 0.24, scroll_reveal: 'fade',
      parallax: false, pointer_interaction: true, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'A celebration woven in time',
      title: 'The curtain rises for our story',
      message: 'Draw back the tapestry and join us for a chapter made to become an heirloom.',
      action_label: 'Draw back the curtain',
    },
    music: { track_id: 'moonlit_strings', volume: 0.24 },
    sections: [
      section('hero', 0, { variant: 'royal_tapestry', overrides: { eyebrow: 'Together with our families' } }),
      section('countdown', 1, { variant: 'woven_numerals', overrides: { headline: 'Until the first toast' } }),
      section('story', 2, { variant: 'illuminated_folio', overrides: { headline: 'A story worth weaving' } }),
      section('itinerary', 3, { variant: 'ceremony_carousel', overrides: { headline: 'The royal programme' } }),
      section('venue', 4, { variant: 'royal_venue', overrides: { headline: 'Where the chapter unfolds' } }),
      section('map', 5, { variant: 'gilded_map' }),
      section('gallery', 6, { variant: 'tapestry_masonry', overrides: { headline: 'Portraits from our story' } }),
      section('wishes', 7, { variant: 'courtly_notes', overrides: { headline: 'Notes from our court' } }),
      section('rsvp', 8, { variant: 'royal_decree', overrides: { headline: 'Reserve your place' } }),
      section('contacts', 9, { variant: 'court_directory' }),
      section('footer', 10, { variant: 'woven_signature' }),
    ],
  }),
  template({
    id: 'galago.kashi-riverlight',
    name: 'Kashi Riverlight',
    description: 'Cross the Ganga at first light, then follow a hand-painted river journey through the wedding celebration.',
    mood: 'Poetic · Riverside',
    swatches: ['#285C63', '#A95638', '#F3E7CF'],
    theme: {
      primary_color: '#285C63', secondary_color: '#D99A3D', accent_color: '#A95638',
      background_color: '#F3E7CF', text_color: '#302621', heading_font: 'Fraunces',
      body_font: 'Libre Baskerville', surface_style: 'flat', corner_style: 'soft', spacing: 'compact',
    },
    layout: {
      mode: 'stacked', hero_panel_position: 'left', hero_panel_width: 48,
      background_image_url: '/assets/web-invites/kashi-riverlight/kashi-river-dawn.jpg',
      frame_image_url: '/assets/web-invites/kashi-riverlight/kashi-banarasi-portal-v1.png', decorative_image_url: '/assets/web-invites/kashi-riverlight/kashi-boat-white-v2.png',
      background_position: 'center', show_frame: true, overlay_strength: 0.22,
      section_style: 'river_paper', section_divider: 'waterline', mobile_hero_height: 'full',
    },
    effects: {
      hero_effect: 'none', intensity: 0.25, speed: 0.16, scroll_reveal: 'fade',
      parallax: false, pointer_interaction: false, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'From one shore to another',
      title: 'Our journey begins in Kashi',
      message: 'Cross the river with us and arrive at a celebration of two lives becoming one.',
      action_label: 'Begin the crossing',
    },
    music: { track_id: 'soft_piano', volume: 0.24 },
    sections: [
      section('hero', 0, { variant: 'kashi_riverfront', overrides: { eyebrow: 'Together with our families' } }),
      section('story', 1, { variant: 'river_letter', overrides: { headline: 'Where our journey meets the river' } }),
      section('countdown', 2, { variant: 'ghat_countdown', overrides: { headline: 'Until we meet by the Ganga' } }),
      section('itinerary', 3, { variant: 'ghat_programme', overrides: { headline: 'A day by the Ganga' } }),
      section('venue', 4, { variant: 'riverfront_places', overrides: { headline: 'Along the riverbank' } }),
      section('map', 5, { variant: 'river_bend_map' }),
      section('gallery', 6, { variant: 'river_editorial', overrides: { headline: 'Moments carried with us' } }),
      section('wishes', 7, { variant: 'ripple_blessings', overrides: { headline: 'Blessings across the water' } }),
      section('rsvp', 8, { variant: 'riverbank_rsvp', overrides: { headline: 'Meet us by the river' } }),
      section('contacts', 9, { variant: 'kashi_directory' }),
      section('footer', 10, { variant: 'dusk_signature', overrides: { body: 'From the banks of the Ganga, with love' } }),
    ],
  }),
  template({
    id: 'galago.classic-elegance',
    name: 'Classic Elegance',
    description: 'Timeless ivory, sage and gold with a graceful editorial rhythm.',
    mood: 'Timeless · Refined',
    swatches: ['#5D7061', '#D4AF37', '#FFFDF8'],
    theme: {
      primary_color: '#5D7061', secondary_color: '#D4AF37', accent_color: '#B76E79',
      background_color: '#FFFDF8', text_color: '#2C2926', heading_font: 'Playfair Display',
      body_font: 'Inter', surface_style: 'paper', corner_style: 'rounded', spacing: 'comfortable',
    },
    effects: {
      hero_effect: 'golden_dust', intensity: 0.85, speed: 0.38, scroll_reveal: 'rise',
      parallax: true, pointer_interaction: false, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'A beautiful day awaits',
      title: 'Our next chapter begins',
      message: 'We would love you to be part of the moments we will remember forever.',
      action_label: 'Open our story',
    },
    music: { track_id: 'soft_piano', volume: 0.3 },
    sections: [
      section('hero', 0), section('countdown', 1), section('story', 2),
      section('itinerary', 3), section('venue', 4), section('gallery', 5),
      section('wishes', 6), section('rsvp', 7), section('contacts', 8), section('footer', 9),
    ],
  }),
  template({
    id: 'galago.phoolon-ki-baarat',
    name: 'Phoolon Ki Baarat',
    description: 'A joyful floral celebration in marigold, rani pink and deep plum.',
    mood: 'Festive · Floral',
    swatches: ['#7A214F', '#E6A23C', '#FFF4E6'],
    theme: {
      primary_color: '#7A214F', secondary_color: '#E6A23C', accent_color: '#D94F70',
      background_color: '#FFF4E6', text_color: '#3A1830', heading_font: 'Cormorant Garamond',
      body_font: 'Inter', surface_style: 'soft', corner_style: 'pill', spacing: 'airy',
    },
    effects: {
      hero_effect: 'petals', intensity: 1.15, speed: 0.44, scroll_reveal: 'scale',
      parallax: true, pointer_interaction: true, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'The festivities are blooming',
      title: 'Come celebrate with us',
      message: 'Color, music and joyful hearts are coming together for one unforgettable celebration.',
      action_label: 'Let the celebration begin',
    },
    music: { track_id: 'garden_waltz', volume: 0.34 },
    sections: [
      section('hero', 0, { variant: 'cinematic' }), section('countdown', 1, { variant: 'statement' }),
      section('hosts', 2), section('story', 3, { variant: 'letter' }), section('itinerary', 4, { variant: 'cards' }),
      section('venue', 5), section('gallery', 6), section('wishes', 7), section('rsvp', 8, { variant: 'banner' }),
      section('registry', 9), section('contacts', 10), section('footer', 11, { variant: 'signature' }),
    ],
  }),
  template({
    id: 'galago.winter-vows',
    name: 'Winter Vows',
    description: 'Moonlit blue, silver and soft snowfall for an intimate winter evening.',
    mood: 'Dreamy · Celestial',
    swatches: ['#24364B', '#B9C7D5', '#F5F8FC'],
    theme: {
      primary_color: '#24364B', secondary_color: '#B9C7D5', accent_color: '#8EA9C1',
      background_color: '#F5F8FC', text_color: '#1B2838', heading_font: 'Libre Baskerville',
      body_font: 'Inter', surface_style: 'glass', corner_style: 'rounded', spacing: 'airy',
    },
    effects: {
      hero_effect: 'snow', intensity: 1.1, speed: 0.3, scroll_reveal: 'fade',
      parallax: true, pointer_interaction: false, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'A little magic is falling',
      title: 'Meet us beneath the stars',
      message: 'A warm celebration awaits in the stillness of a winter evening.',
      action_label: 'Enter our winter story',
    },
    music: { track_id: 'moonlit_strings', volume: 0.27 },
    sections: [
      section('hero', 0, { variant: 'cinematic' }), section('countdown', 1), section('story', 2, { variant: 'quote' }),
      section('itinerary', 3), section('venue', 4, { variant: 'feature' }), section('gallery', 5, { variant: 'filmstrip' }),
      section('wishes', 6, { variant: 'quote' }), section('rsvp', 7), section('footer', 8, { variant: 'statement' }),
    ],
  }),
  template({
    id: 'galago.modern-minimal',
    name: 'Modern Minimal',
    description: 'Quiet typography, generous space and warm neutrals that keep the focus personal.',
    mood: 'Modern · Understated',
    swatches: ['#1F2933', '#C59D5F', '#F7F4EF'],
    theme: {
      primary_color: '#1F2933', secondary_color: '#C59D5F', accent_color: '#9B6F58',
      background_color: '#F7F4EF', text_color: '#1F2933', heading_font: 'DM Serif Display',
      body_font: 'Inter', surface_style: 'flat', corner_style: 'square', spacing: 'airy',
    },
    effects: {
      hero_effect: 'none', intensity: 0.2, speed: 0.5, scroll_reveal: 'rise',
      parallax: false, pointer_interaction: false, reduce_motion_fallback: true,
    },
    opening: {
      eyebrow: 'Save a moment for us',
      title: 'A simple beginning',
      message: 'The people we love are all we need to make this day complete.',
      action_label: 'View the celebration',
    },
    music: { track_id: 'soft_piano', volume: 0.24 },
    sections: [
      section('hero', 0, { variant: 'minimal' }), section('countdown', 1, { variant: 'inline' }),
      section('story', 2), section('itinerary', 3, { variant: 'compact' }), section('venue', 4, { variant: 'minimal' }),
      section('gallery', 5, { variant: 'editorial' }), section('rsvp', 6, { variant: 'minimal' }),
      section('contacts', 7, { variant: 'minimal' }), section('footer', 8),
    ],
  }),
];

export const DEFAULT_WEB_INVITE_TEMPLATE_ID = 'galago.modern-minimal';

export const cloneInviteConfig = (config) => JSON.parse(JSON.stringify(config));

const TEMPLE_BRASS_V2_ASSETS = {
  background: '/assets/web-invites/south-indian-temple-brass/temple-dawn-courtyard-v2.jpg',
  bell: '/assets/web-invites/south-indian-temple-brass/temple-bell-v2.png',
};

const TEMPLE_BRASS_V1_VARIANTS = {
  temple_gateway: 'temple_courtyard',
  brass_bells: 'muhurtham_dial',
  palm_leaf_letter: 'stone_prologue',
  lamp_procession: 'muhurtham_program',
  mandapam_feature: 'temple_inscriptions',
  brass_map: 'kolam_map',
  temple_windows: 'temple_slideshow',
  blessing_scrolls: 'blessing_chorus',
  lotus_seal: 'betel_leaf',
  brass_inscriptions: 'family_directory',
  deepam_signature: 'courtyard_signature',
};

function migrateTempleBrassConfig(config) {
  if (config.template_id !== 'galago.south-indian-temple-brass') return config;
  const layout = { ...(config.layout || {}) };
  if (layout.background_image_url === '/assets/web-invites/south-indian-temple-brass/temple-courtyard-mobile.jpg') layout.background_image_url = TEMPLE_BRASS_V2_ASSETS.background;
  if (layout.frame_image_url === '/assets/web-invites/south-indian-temple-brass/brass-torana-mobile.png') {
    layout.frame_image_url = null;
    layout.show_frame = false;
  }
  if (layout.decorative_image_url === '/assets/web-invites/south-indian-temple-brass/deepam-urli-mobile.png') layout.decorative_image_url = TEMPLE_BRASS_V2_ASSETS.bell;
  if (layout.section_style === 'temple_parchment') layout.section_style = 'temple_courtyard';
  if (layout.section_divider === 'temple_arch') layout.section_divider = 'temple_steps';

  return {
    ...config,
    template_version: Math.max(2, Number(config.template_version) || 1),
    layout,
    opening: {
      ...(config.opening || {}),
      ...(config.opening?.action_label === 'Light the first lamp' ? { action_label: 'Ring the wedding bell' } : {}),
    },
    sections: (config.sections || []).map((item) => ({ ...item, variant: TEMPLE_BRASS_V1_VARIANTS[item.variant] || item.variant })),
  };
}

export function completeInviteSections(config) {
  const cloned = migrateTempleBrassConfig(cloneInviteConfig(config));
  cloned.layout = { ...DEFAULT_WEB_INVITE_LAYOUT, ...(cloned.layout || {}), mode: 'stacked' };
  cloned.opening = { ...DEFAULT_WEB_INVITE_OPENING, ...(cloned.opening || {}) };
  cloned.music = { ...DEFAULT_WEB_INVITE_MUSIC, ...(cloned.music || {}) };
  if (BIRTHDAY_TEMPLATE_IDS.has(cloned.template_id)) {
    cloned.sections = (cloned.sections || []).filter((item) => item.type !== 'venue');
  }
  const existingTypes = new Set(cloned.sections.map((item) => item.type));
  const sections = [...cloned.sections];

  WEB_INVITE_SECTION_CATALOG.forEach((definition) => {
    if (!existingTypes.has(definition.type)) {
      sections.push(section(definition.type, sections.length, { enabled: false }));
    }
  });

  cloned.sections = sections
    .sort((a, b) => a.order - b.order)
    .map((item, order) => ({ ...item, order }));
  return cloned;
}

export function createCustomSection(order) {
  return {
    id: `custom-${Date.now()}`,
    type: 'custom',
    enabled: true,
    order,
    variant: 'editorial',
    source_mode: 'custom',
    settings: {},
    overrides: { headline: 'A little something special', body: 'Add your personal message here.' },
  };
}
